import type { PayablePoolToken } from '@ui/types/token';

import { leverupBrand } from './brands/leverup';
import { mushystudioBrand } from './brands/mushystudio';
import type {
  BrandConfig as BrandConfigType,
  BrandId as BrandIdType,
  BrandNavItemKey as BrandNavItemKeyType,
} from './brands/types';

export type BrandConfig = BrandConfigType;
export type BrandId = BrandIdType;
export type BrandNavItemKey = BrandNavItemKeyType;

export const DEFAULT_BRAND_ID: BrandId = 'leverup';

export const BRAND_CONFIGS: Record<BrandId, BrandConfig> = {
  leverup: leverupBrand,
  mushystudio: mushystudioBrand,
};

export const getBrandById = (brandId: BrandId): BrandConfig =>
  BRAND_CONFIGS[brandId] ?? BRAND_CONFIGS[DEFAULT_BRAND_ID];

export const normalizeHost = (host: string): string =>
  host
    .trim()
    .toLowerCase()
    .replace(/:\d+$/, '')
    .replace(/^www\./, '');

export const getBrandByHost = (host?: string | null): BrandConfig => {
  if (!host) return getBrandById(DEFAULT_BRAND_ID);

  const normalizedHost = normalizeHost(host);
  const matchedBrand = Object.values(BRAND_CONFIGS).find((brand) =>
    brand.hosts.some((item) => normalizeHost(item) === normalizedHost),
  );

  return matchedBrand ?? getBrandById(DEFAULT_BRAND_ID);
};

export const getBrandByEnv = (
  brandIdFromEnv?: string | null,
): BrandConfig | null => {
  if (!brandIdFromEnv) return null;

  const normalizedBrandId = brandIdFromEnv.trim().toLowerCase();
  const matchedBrand = Object.values(BRAND_CONFIGS).find(
    (brand) => brand.id.toLowerCase() === normalizedBrandId,
  );

  return matchedBrand ?? null;
};

export const resolveBrand = ({
  host,
  brandIdFromEnv,
}: {
  host?: string | null;
  brandIdFromEnv?: string | null;
}): BrandConfig => {
  const brandFromEnv = getBrandByEnv(brandIdFromEnv);
  if (brandFromEnv) return brandFromEnv;

  return getBrandByHost(host);
};

export const filterAnyCollateralPayableTokens = (
  payableTokens: PayablePoolToken[],
  brand: BrandConfig,
): PayablePoolToken[] => {
  const supportedSymbols = new Set(brand.collateralTokenSymbols);

  return payableTokens.filter((token) => supportedSymbols.has(token.symbol));
};

